import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Sun, Droplets, Heart, Loader } from 'lucide-react';
import parse from 'html-react-parser';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { COACH_CONFIG } from '../../coachconfig';

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Coach ID from config
  const { coachUserId, apiBaseUrl } = COACH_CONFIG;

  // Check if post data was passed via navigation state
  useEffect(() => {
    if (location.state?.post) {
      // Use the post data passed from the Blog page
      setPost(location.state.post);
      setLoading(false);
    } else {
      // Fallback: fetch specific blog post from API
      fetchBlogPost();
    }
  }, [location.state, id]);

  // Fetch specific blog post from API (fallback)
  const fetchBlogPost = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${apiBaseUrl}/blog/${id}?coachUserId=${coachUserId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setPost(result.blogPost);
      } else {
        console.error('Error fetching blog post:', result.error);
        setError(result.error || 'Failed to load blog post');
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError('Failed to load blog post. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to render icon based on icon string or default
  const renderIcon = (iconString) => {
    // Map common icon strings to Lucide icons
    const iconMap = {
      'sun': <Sun className="text-sky-600" size={24} />,
      'droplets': <Droplets className="text-sky-600" size={24} />,
      'heart': <Heart className="text-sky-600" size={24} />,
    };

    // If it's a known icon string, return the component
    if (iconString && iconMap[iconString.toLowerCase()]) {
      return iconMap[iconString.toLowerCase()];
    }

    // If it's an emoji, return it as text
    if (iconString && /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(iconString)) {
      return <span className="text-3xl">{iconString}</span>;
    }

    // Default icon
    return <Sun className="text-sky-600" size={24} />;
  };

  // Custom options for html-react-parser
  const parseOptions = {
    replace: (domNode) => {
      if (domNode.type === 'tag' && domNode.name === 'h1') {
        return (
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {domNode.children[0]?.data}
          </h1>
        );
      }
      if (domNode.type === 'tag' && domNode.name === 'h2') {
        return (
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 mt-8">
            {domNode.children[0]?.data}
          </h2>
        );
      }
      if (domNode.type === 'tag' && domNode.name === 'h3') {
        return (
          <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-3 mt-6">
            {domNode.children[0]?.data}
          </h3>
        );
      }
      if (domNode.type === 'tag' && domNode.name === 'h4') {
        return (
          <h4 className="text-xl md:text-2xl font-semibold text-sky-600 mb-2 mt-4">
            {domNode.children[0]?.data}
          </h4>
        );
      }
      if (domNode.type === 'tag' && domNode.name === 'p') {
        return (
          <p className="text-gray-700 leading-relaxed mb-4 text-lg">
            {domNode.children?.map((child, index) => {
              if (child.type === 'text') {
                return child.data;
              }
              if (child.type === 'tag' && child.name === 'strong') {
                return (
                  <strong key={index} className="text-gray-900 font-semibold">
                    {child.children[0]?.data}
                  </strong>
                );
              }
              if (child.type === 'tag' && child.name === 'em') {
                return (
                  <em key={index} className="text-sky-600 italic">
                    {child.children[0]?.data}
                  </em>
                );
              }
              return child.data;
            })}
          </p>
        );
      }
      if (domNode.type === 'tag' && domNode.name === 'ul') {
        return (
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
            {domNode.children?.map((child, index) => {
              if (child.type === 'tag' && child.name === 'li') {
                return (
                  <li key={index} className="text-lg">
                    {child.children?.map((grandChild, grandIndex) => {
                      if (grandChild.type === 'text') {
                        return grandChild.data;
                      }
                      if (grandChild.type === 'tag' && grandChild.name === 'strong') {
                        return (
                          <strong key={grandIndex} className="text-gray-900 font-semibold">
                            {grandChild.children[0]?.data}
                          </strong>
                        );
                      }
                      return grandChild.data;
                    })}
                  </li>
                );
              }
              return null;
            })}
          </ul>
        );
      }
      if (domNode.type === 'tag' && domNode.name === 'ol') {
        return (
          <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-2">
            {domNode.children?.map((child, index) => {
              if (child.type === 'tag' && child.name === 'li') {
                return (
                  <li key={index} className="text-lg">
                    {child.children?.map((grandChild, grandIndex) => {
                      if (grandChild.type === 'text') {
                        return grandChild.data;
                      }
                      if (grandChild.type === 'tag' && grandChild.name === 'strong') {
                        return (
                          <strong key={grandIndex} className="text-gray-900 font-semibold">
                            {grandChild.children[0]?.data}
                          </strong>
                        );
                      }
                      return grandChild.data;
                    })}
                  </li>
                );
              }
              return null;
            })}
          </ol>
        );
      }
      if (domNode.type === 'tag' && domNode.name === 'blockquote') {
        return (
          <blockquote className="border-l-4 border-sky-600 pl-4 italic text-gray-700 mb-4 text-lg bg-sky-50 py-4 rounded-r-lg">
            {domNode.children[0]?.data}
          </blockquote>
        );
      }
      if (domNode.type === 'tag' && domNode.name === 'hr') {
        return (
          <hr className="border-sky-200 my-8" />
        );
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-white">
        <Header />
        <main className="flex-grow flex items-center justify-center py-20">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin text-sky-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading blog post...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex flex-col min-h-screen bg-white">
        <Header />
        <main className="flex-grow flex items-center justify-center py-20">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {error ? 'Error loading post' : 'Post not found'}
            </h1>
            {error && <p className="text-red-600 mb-4">{error}</p>}
            <button 
              onClick={() => navigate('/blog')}
              className="text-sky-600 hover:text-sky-700 transition-colors"
            >
              Return to Blog
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-20 sm:py-24 md:py-32 bg-gradient-to-br from-sky-50 to-blue-50">
          <div className="max-w-4xl mx-auto px-4">
            <button 
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-600 hover:text-sky-600 mb-8 transition-colors duration-300"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </button>

            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                {renderIcon(post.icon)}
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900">
                  {post.title}
                </h1>
              </div>
              
              <div className="flex justify-center items-center gap-6 text-gray-500 mb-6">
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span>{post.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} />
                  <span>{post.readTime}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4">
            <div className="relative h-[400px] w-full mb-12 rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/success1.webp'; // Fallback image
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>

            <div className="bg-white border border-sky-100 rounded-2xl p-8 shadow-sm">
              <div className="prose max-w-none">
                {parse(post.content, parseOptions)}
              </div>
            </div>

            {/* Back to Blog Button */}
            <div className="text-center mt-12">
              <button 
                onClick={() => navigate('/blog')}
                className="bg-sky-600 text-white px-8 py-3 rounded-xl hover:bg-sky-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Back to All Articles
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogDetails; 